import React, { Fragment, forwardRef, ButtonHTMLAttributes, ReactNode } from 'react';
import BEMHelper from 'react-bem-helper';

import Loader from '../Loader';
import '../../common/fonts.less';
import './style.less';

const classes = new BEMHelper('Button');

type ButtonVariant =
    | 'container'
    | 'contained'
    | 'big'
    | 'big-outline'
    | 'text'
    | 'text-only'
    | 'small-text'
    | 'outlined'
    | 'sign-in'
    | 'join'
    | 'start'
    | 'small-input'
    | 'light-clear'
    | 'uppercase'
    | 'round'
    | 'bland'
    | 'promo'
    | 'confirm'
    | 'reaction'
    | 'outline'
    | 'with-loader' // specific variant that should not be used externally
    | 'preview'
;

// Relaxed focus handler type for backward compatibility with JSX code
// that passes callbacks with different signatures (e.g. (value: string) => void)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RelaxedFocusHandler = (event?: any) => void;

export type { ButtonVariant };

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onFocus' | 'onBlur'> {
  variant?: ButtonVariant | `${ButtonVariant}${string}`;
  className?: string;
  children?: ReactNode;
  loading?: boolean;
  disabled?: boolean;
  onFocus?: RelaxedFocusHandler;
  onBlur?: RelaxedFocusHandler;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ variant = 'contained', className = '', children, loading, disabled, ...props }, ref) => {
        const classModifiers = Array.isArray(variant) ? variant : [variant];
        // Loader was tested only with a rounded and default button.
        // If needed to use it with other types of buttons, it may require additional work to support that functionality.
        if (loading) {
            classModifiers.push('with-loader');
        }

        return (
            <button
                {...classes('', classModifiers, className)}
                ref={ref}
                disabled={disabled || loading}
                {...props}>
                {loading ? (
                    <div {...classes('with-loader-content')}>
                        <Loader />
                        <div {...classes('with-loader-content-children')}>{children}</div>
                    </div>
                ) : (
                    <Fragment>{children}</Fragment>
                )}
            </button>
        );
    }
);

Button.displayName = 'Button';

export default Button;
